// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { styled, Theme } from "@mui/material/styles";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Typography from "@mui/material/Typography";
import Toolbar from "@mui/material/Toolbar";
import { selectDecodedIdToken } from "@slices/authSlice";
import { useSelector } from "react-redux";
import { SIDEBAR_WIDTH } from "@config/ui";
import { Avatar, Box, Chip, Menu, MenuItem, Stack, Tooltip } from "@mui/material";
import { tokens } from "../../theme";
import { RootState, useAppSelector } from "@slices/store";
import { DeployedEnvironment } from "@config/config";
import { APP_NAME } from "@config/config";
import React from "react";
import { useAsgardeo } from "@asgardeo/react";

interface HeaderProps {
  open: boolean;
  theme: Theme;
  title: string;
  email?: string;
}

const Header = (props: HeaderProps) => {
  const { signOut } = useAsgardeo();
  const decodedIdToken = useSelector(selectDecodedIdToken);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const userRoles = useAppSelector((state: RootState) => state.auth.roles);
  const employeeInfo = useAppSelector(
    (state: RootState) => state.auth.employeeInfo
  );

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <AppBar position="fixed" open={props.open}>
      <Toolbar>
        <Typography
          variant="h5"
          noWrap
          component="div"
          sx={{
            flexGrow: 1,
            display: { xs: "none", sm: "block" },
            fontWeight: 600,
          }}
        >
          {!props.open ? APP_NAME : ""}
        </Typography>

        {DeployedEnvironment && (
          <Chip
            sx={{
              background: (theme) =>
                tokens(theme.palette.mode).customColors.lightGreen,
              color: (theme) => theme.palette.common.white,
              mr: 1,
            }}
            label={
              <>
                env: <b> {DeployedEnvironment}</b>
              </>
            }
            style={{ borderRadius: "0px" }}
            size="medium"
          />
        )}
          <Box sx={{ flexGrow: 0 }}>
          <>
              {employeeInfo ? (
                 <>
                 <Stack flexDirection={"row"} alignItems={"center"} gap={2}>
                   <Box>
                     <Typography variant="body1" sx={{ fontWeight: 600 }}>
                       {employeeInfo?.firstName + " " + employeeInfo?.lastName}
                     </Typography>                   
                   </Box>
                   <Tooltip title="Open settings">
                     <Avatar
                       onClick={handleOpenUserMenu}
                       sx={{ border: 1, borderColor: "primary.main" }}
                       src={employeeInfo?.employeeThumbnail || ""}
                       alt={employeeInfo?.firstName || "Avatar"}
                     >
                       {employeeInfo?.firstName?.charAt(0)}
                     </Avatar>
                   </Tooltip>
                 </Stack>
                    <Menu
                   sx={{ mt: "45px" }}
                   id="menu-appbar"
                   anchorEl={anchorElUser}
                   anchorOrigin={{
                     vertical: "top",
                     horizontal: "right",
                   }}
                   keepMounted
                   transformOrigin={{
                     vertical: "top",
                     horizontal: "right",
                   }}
                   open={Boolean(anchorElUser)}
                   onClose={handleCloseUserMenu}
                 >
                   <MenuItem
                     key={"logout"}
                     onClick={() => {
                     signOut().catch(() => {});
                     }}
                   >
                     <Typography textAlign="center">Logout</Typography>
                   </MenuItem>
                 </Menu>
               </>
              ) : (
                <Typography>{decodedIdToken?.email}</Typography>
              )}
            </>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  marginLeft: SIDEBAR_WIDTH,
  color: "white",
  background:
    theme.palette.mode === "light"
      ? tokens(theme.palette.mode).customColors.headerBarLight
      : tokens(theme.palette.mode).customColors.bottomBarDark,
  boxShadow: "none",
  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  width: `calc(100% - calc(${theme.spacing(9)} + 1px))`,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: SIDEBAR_WIDTH,
    width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));
